/**
 * Chat Hooks
 * User-scoped chat state backed by SmartCache + localStorage persistence.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { smartCache, presentationRepository } from '../../services/data';
import {
  isChatSyncEnabled,
  subscribeChatList,
  subscribeChatMessages,
  fetchChatMessages,
  upsertChat,
  deleteChatRemote
} from '../../services/data/ChatSyncService';
import {
  LOCKER_SCOPED_SAFE_CHAT_TITLE,
  TRANSCRIPT_MODE_LOCKER_SCOPED,
  TRANSCRIPT_MODE_ORDINARY,
  TRANSCRIPT_MODE_PENDING,
  chatsForOwnerReadablePersistence,
  isLockerScopedTranscript,
  isTranscriptPolicyPending,
  ownerReadableTranscriptAllowed,
  reconcileServerTranscriptMode,
  transcriptModeForChat,
} from '../../services/data/chatTranscriptPolicy';
import {
  deleteScopedTranscript,
  subscribeScopedTranscript,
} from '../../services/data/scopedTranscriptService';
import { deleteOpencodeChatHistory } from '../../services/data/opencodeHistoryService';
import {
  mergeAuthoritativeMessages,
  mergeRemoteMessages,
  selectMessagesToPublish,
  recordPublishedRevs,
  chatMetaFingerprint
} from '../../services/data/transcriptMerge';
import { useAuth } from '../UserContext';
import { useUI } from '../UIContext';
import { mapRawTodos } from '../../../screens/chat/ai-chat/utils';

// ---------------------------------------------------------------------------
// localStorage persistence helpers
// ---------------------------------------------------------------------------
const CHAT_STORAGE_KEY_PREFIX = 'restack_chats_';
const CHAT_STORAGE_VERSION = 1;
const MAX_CHATS_TO_PERSIST = 50;
const QUOTA_FALLBACK_CHATS_TO_PERSIST = 10;
const MAX_MESSAGES_PER_CHAT = 200;
// Sync flush cadence (trailing throttle, NOT a debounce: a streaming turn
// changes the cache every second, and a debounce that resets on each change
// never fires until the turn pauses, stalling follower panes a full turn
// behind). Tight while this pane is streaming so peers feel live.
const STREAMING_FLUSH_MS = 1000;
const IDLE_FLUSH_MS = 2000;

function getChatStorageKey(userId) {
  return `${CHAT_STORAGE_KEY_PREFIX}${userId}`;
}

function loadPersistedChats(userId) {
  try {
    const raw = window.localStorage.getItem(getChatStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CHAT_STORAGE_VERSION || !Array.isArray(parsed.chats)) {
      return [];
    }
    const safeChats = chatsForOwnerReadablePersistence(parsed.chats);
    if (JSON.stringify(safeChats) !== JSON.stringify(parsed.chats)) {
      window.localStorage.setItem(
        getChatStorageKey(userId),
        JSON.stringify({ version: CHAT_STORAGE_VERSION, chats: safeChats })
      );
    }
    return safeChats;
  } catch {
    return [];
  }
}

// Components that carry user-visible state we MUST keep across reloads.
// Anything else (toolStatus, tool_call, agentTimeline, slideWork progress)
// can be rebuilt from metadata or simply omitted under quota pressure.
const ESSENTIAL_COMPONENT_TYPES = new Set([
  'text',
  'research',
  'citations',
  'report',
  'createSlides',
  'editSlides',
  'attachments',
]);

// Trim the largest text fields on a research card if it's the saved component
// blowing the budget. Keep citations + searchQueries fully — they're small
// and carry the user-visible "I researched X" trail.
function trimResearchComponent(component) {
  if (component?.type !== 'research' || !component.data) return component;
  const data = { ...component.data };
  // Cap fullReport / report at 8 KB on the saved copy. Streaming session
  // re-fetches the full text from the backend; on reload this preserves
  // the gist of the report without blowing localStorage.
  const TEXT_CAP = 8000;
  if (typeof data.fullReport === 'string' && data.fullReport.length > TEXT_CAP) {
    data.fullReport = data.fullReport.slice(0, TEXT_CAP) + '\n…[truncated]';
  }
  if (typeof data.report === 'string' && data.report.length > TEXT_CAP) {
    data.report = data.report.slice(0, TEXT_CAP) + '\n…[truncated]';
  }
  return { ...component, data };
}

// Recency of a chat in ms. Local chats carry `updatedAt` (SmartCache stamps it
// on create/addMessage/updateChat) and the chat-list hydrate maps Firestore's
// `lastMessageAt` onto the same field; `createdAt` is the fallback for a chat
// that has never been touched. Same field order as the sidebar sort. A chat
// with neither timestamp resolves to 0: it sorts oldest, but it is still a
// candidate for persistence rather than being filtered out.
function chatRecencyMs(chat) {
  const toMs = (value) => {
    if (Number.isFinite(value)) return value;
    const parsed = typeof value === 'string' ? Date.parse(value) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  };
  return toMs(chat?.updatedAt) ?? toMs(chat?.createdAt) ?? 0;
}

// The chats array reaches persistence in whatever order its producer used:
// newest-first after a Firestore chat-list hydrate, append-order from the
// local cache. Sort by the chat's own recency before capping so the newest
// chats are the ones kept regardless of the incoming order.
function mostRecentChats(chats, limit) {
  return [...chats]
    .sort((a, b) => chatRecencyMs(b) - chatRecencyMs(a))
    .slice(0, limit);
}

function savePersistedChats(userId, chats) {
  if (!userId || !Array.isArray(chats)) return;
  const ownerReadableChats = chatsForOwnerReadablePersistence(chats);

  // First attempt: keep everything except welcome_cards (purely UI bootstrap)
  try {
    const trimmed = mostRecentChats(ownerReadableChats, MAX_CHATS_TO_PERSIST).map(chat => ({
      ...chat,
      messages: (chat.messages || []).slice(-MAX_MESSAGES_PER_CHAT).map(msg => {
        if (!Array.isArray(msg.components)) return msg;
        const filtered = msg.components.filter(c => c.type !== 'welcome_cards');
        return { ...msg, components: filtered.length > 0 ? filtered : undefined };
      }),
    }));
    const payload = JSON.stringify({ version: CHAT_STORAGE_VERSION, chats: trimmed });
    window.localStorage.setItem(getChatStorageKey(userId), payload);
    return;
  } catch (err) {
    if (err?.name !== 'QuotaExceededError') {
      // Non-quota error; nothing else to try
      return;
    }
  }

  // Quota fallback #1: keep ESSENTIAL components only (drop toolStatus,
  // tool_call timelines, etc.). Trim oversized research report text. This
  // preserves the research card, citations, slide cards across a reload.
  try {
    const slim = mostRecentChats(ownerReadableChats, MAX_CHATS_TO_PERSIST).map(chat => ({
      ...chat,
      messages: (chat.messages || []).slice(-MAX_MESSAGES_PER_CHAT).map(msg => {
        if (!Array.isArray(msg.components)) return msg;
        const essential = msg.components
          .filter(c => ESSENTIAL_COMPONENT_TYPES.has(c?.type))
          .map(trimResearchComponent);
        return { ...msg, components: essential.length > 0 ? essential : undefined };
      }),
    }));
    window.localStorage.setItem(
      getChatStorageKey(userId),
      JSON.stringify({ version: CHAT_STORAGE_VERSION, chats: slim })
    );
    return;
  } catch (err) {
    if (err?.name !== 'QuotaExceededError') return;
  }

  // Quota fallback #2: drop components entirely on the oldest messages,
  // keep the most recent 20 messages with essential components only. The
  // metadata on each message still has enough for rebuildAssistantComponents
  // to reconstruct a research card from metadata.action === 'research'.
  try {
    const fewer = mostRecentChats(ownerReadableChats, QUOTA_FALLBACK_CHATS_TO_PERSIST).map(chat => {
      const messages = chat.messages || [];
      const recentCount = 20;
      const head = messages.slice(0, Math.max(0, messages.length - recentCount))
        .map(({ components, ...rest }) => rest);
      const tail = messages.slice(-recentCount).map(msg => {
        if (!Array.isArray(msg.components)) return msg;
        const essential = msg.components
          .filter(c => ESSENTIAL_COMPONENT_TYPES.has(c?.type))
          .map(trimResearchComponent);
        return { ...msg, components: essential.length > 0 ? essential : undefined };
      });
      return { ...chat, messages: [...head, ...tail] };
    });
    window.localStorage.setItem(
      getChatStorageKey(userId),
      JSON.stringify({ version: CHAT_STORAGE_VERSION, chats: fewer })
    );
  } catch {
    // Give up silently — in-memory state still works for this session
  }
}

// ---------------------------------------------------------------------------
// Message normalization helpers
// ---------------------------------------------------------------------------

const mergeTodoListIntoPlan = (message) => {
  if (!message || !message.metadata) {
    return message;
  }

  const todoList = message.metadata.todoList;
  if (!todoList || !Array.isArray(todoList.items) || todoList.items.length === 0) {
    return message;
  }

  const planMeta = message.metadata.plan || {};
  const incomingVersion = todoList.meta?.version ?? null;
  const existingVersion = planMeta.todoMeta?.version ?? null;

  if (Array.isArray(planMeta.todo) && planMeta.todo.length > 0 && existingVersion === incomingVersion) {
    return message;
  }

  const normalizedTodos = mapRawTodos(todoList.items);

  const mergedPlan = {
    ...planMeta,
    todo: normalizedTodos,
    todoMeta: {
      ...(planMeta.todoMeta || {}),
      ...(todoList.meta || {}),
      version: incomingVersion,
      status: (todoList.meta?.status || planMeta.todoMeta?.status || 'update'),
      updatedAt: todoList.meta?.updatedAt || planMeta.todoMeta?.updatedAt || null,
      source: todoList.meta?.source || planMeta.todoMeta?.source || null,
      activeTodoId: todoList.meta?.activeTodoId || planMeta.todoMeta?.activeTodoId || null
    }
  };

  return {
    ...message,
    metadata: {
      ...message.metadata,
      plan: mergedPlan
    }
  };
};

const coerceTimestamp = (value, fallback = Date.now()) => {
  if (Number.isFinite(value)) {
    return value;
  }
  const parsed = typeof value === 'string' ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const finalizeStreamingMetadata = (message) => {
  if (!message?.metadata) {
    return message;
  }

  const metadata = message.metadata;
  if (!metadata.isStreaming || metadata.isComplete) {
    return message;
  }

  // Recency guard: a RECENT streaming message is (very likely) a live turn,
  // either this pane's own or a peer's arriving through the chat mirror.
  // Leave it streaming for display; only genuinely stale copies (abandoned
  // sessions, dead panes) get settled here.
  const startedMs = coerceTimestamp(metadata.startTime, coerceTimestamp(message.timestamp, 0));
  if (startedMs && Date.now() - startedMs < 5 * 60 * 1000) {
    return message;
  }

  const endTime = metadata.endTime || coerceTimestamp(message.timestamp);
  const startTime = coerceTimestamp(metadata.startTime, endTime);
  const durationSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));

  return {
    ...message,
    metadata: {
      ...metadata,
      isStreaming: false,
      endTime,
      durationSeconds,
      statusLabel: metadata.statusLabel || 'Finished working',
      statusHint: null
    }
  };
};

const normalizeMessages = (messages = []) => messages
  .map(mergeTodoListIntoPlan)
  .map(finalizeStreamingMetadata);

// Live-ownership predicate shared by every merge/publish site in this hook.
const isMessageOwned = (id) => smartCache.isLiveMessageOwned(id);

// ---------------------------------------------------------------------------
// usePresentationChats — self-contained chat hook
// ---------------------------------------------------------------------------

/**
 * User-scoped chat state. Presentations are no longer a Firestore entity —
 * "the presentation" is the user's local PowerPoint file. Chats live
 * directly under the authenticated user (`users/{uid}/chats/{chatId}` on
 * the backend; the in-memory SmartCache below is keyed by `userId` and
 * treats it as an opaque scope identifier).
 *
 * Seeds SmartCache on first mount (hydrating from localStorage),
 * subscribes to SmartCache for live updates, and auto-persists chats to
 * localStorage on changes (debounced).
 */
export function usePresentationChats() {
  const { currentUser } = useAuth();
  const { currentChatId, switchToChat: switchToChatUI } = useUI();
  const [chats, setChats] = useState([]);
  const [currentMessages, setCurrentMessages] = useState([]);

  // Scope key for SmartCache — the authenticated user's uid.
  // `presentationId` is kept as an alias for backwards-compat with the
  // downstream hook signatures that haven't been renamed yet; it contains
  // only the bare userId now (no `plugin-` prefix).
  const userId = currentUser?.uid;
  const presentationId = userId || null;

  // Ref to always have the latest currentChatId inside subscription callbacks
  const currentChatIdRef = useRef(currentChatId);
  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  // Scope-bound transcript content is volatile UI state. It never enters
  // SmartCache, localStorage, or the owner-readable Firestore chat path.
  const volatileTranscriptRef = useRef(new Map());
  const volatileTranscriptOwnerRef = useRef(userId);
  const [scopedTranscriptState, setScopedTranscriptState] = useState('idle');
  const replaceVolatileTranscript = useCallback((chatId, messages) => {
    const safeMessages = Array.isArray(messages) ? normalizeMessages(messages) : [];
    volatileTranscriptRef.current.set(chatId, safeMessages);
    const newestTimestamp = safeMessages[safeMessages.length - 1]?.timestamp;
    const cachedChat = (smartCache.get(presentationId)?.chats || [])
      .find((chat) => chat.id === chatId);
    if (
      cachedChat
      && (
        cachedChat.messageCount !== safeMessages.length
        || (newestTimestamp && cachedChat.updatedAt !== newestTimestamp)
      )
    ) {
      smartCache.updateChat(presentationId, chatId, {
        messageCount: safeMessages.length,
        ...(newestTimestamp ? { updatedAt: newestTimestamp } : {}),
      });
    }
    if (currentChatIdRef.current === chatId) {
      setCurrentMessages(safeMessages);
    }
  }, [presentationId]);
  const clearVolatileTranscript = useCallback((chatId) => {
    volatileTranscriptRef.current.delete(chatId);
    if (currentChatIdRef.current === chatId) {
      setCurrentMessages([]);
    }
  }, []);
  const upsertVolatileTranscriptMessage = useCallback((chatId, message) => {
    if (!chatId || !message) return null;
    const existing = volatileTranscriptRef.current.get(chatId) || [];
    const messageId = message.id || `msg-${Date.now()}`;
    const index = existing.findIndex((candidate) => candidate.id === messageId);
    let next;
    if (index >= 0) {
      next = [...existing];
      next[index] = {
        ...next[index],
        ...message,
        id: messageId,
        timestamp: next[index].timestamp || message.timestamp || new Date().toISOString(),
        rev: Date.now(),
      };
    } else {
      next = [
        ...existing,
        {
          ...message,
          id: messageId,
          timestamp: message.timestamp || new Date().toISOString(),
          rev: Date.now(),
        },
      ];
    }
    replaceVolatileTranscript(chatId, next);
    return next;
  }, [replaceVolatileTranscript]);

  useEffect(() => {
    if (volatileTranscriptOwnerRef.current === userId) return;
    volatileTranscriptOwnerRef.current = userId;
    volatileTranscriptRef.current.clear();
    setCurrentMessages([]);
    setScopedTranscriptState('idle');
  }, [userId]);

  // Per-chat, per-MESSAGE publish cursor: chatId -> Map<messageId, rev>. The
  // flush publishes any message whose rev is newer than its cursor entry;
  // the mirror and the chat-list hydrate record downloaded copies so remote
  // data is never echoed straight back. Per-message (not a chat-level
  // watermark) so a later message written mid-turn can never block an older
  // still-streaming bubble from publishing its updates and final copy.
  const publishedRevsRef = useRef(new Map());
  const getPublishedMap = (chatId) => {
    let map = publishedRevsRef.current.get(chatId);
    if (!map) {
      map = new Map();
      publishedRevsRef.current.set(chatId, map);
    }
    return map;
  };

  // Serializes chat-list hydrate runs: Firestore can fire the async snapshot
  // callback again before the previous run finishes, and out-of-order or
  // interleaved runs could write stale captured state over newer live state.
  const hydrateQueueRef = useRef(Promise.resolve());

  // True once the FIRST chat-list hydrate has merged remote state into the
  // cache. Firestore uploads are gated on it: a pane seeded from stale
  // localStorage must never publish that state over newer remote data it
  // simply has not downloaded yet.
  const hydratedOnceRef = useRef(false);

  // Per-chat marker of the remote lastMessageAt this pane last hydrated.
  // Skips message re-fetches for chats whose remote meta has not advanced
  // (the live mirror keeps the ACTIVE chat fresh in between).
  const hydrateMarkersRef = useRef(new Map());

  // Set of chatIds currently in cache — used to detect deletes (chatId disappears
  // from the chats array) so we can mirror the delete to Firestore. Deletes are
  // accumulated until the next flush (the throttle no longer rebinds a closure
  // per notification).
  const previousChatsRef = useRef(new Map());
  const pendingRemoteDeletesRef = useRef(new Map());

  // Per-chat metadata fingerprint of the last meta doc we wrote. A follower
  // pane merges remote data every second while a peer streams; without this
  // gate every merge would echo an (identical) meta write back to Firestore.
  const metaFingerprintRef = useRef(new Map());

  // Last localStorage save (ms): full-history saves are throttled while a
  // turn is streaming (see the flush below).
  const lastLocalSaveRef = useRef(0);

  // Seed SmartCache from localStorage on first mount
  useEffect(() => {
    if (!presentationId || !userId) return;
    if (!smartCache.get(presentationId)) {
      const persistedChats = loadPersistedChats(userId);
      smartCache.set(presentationId, {
        id: presentationId,
        title: 'PowerPoint Plugin',
        chats: persistedChats,
        userId,
      });
      console.log('[Chat] Seeded SmartCache for', presentationId, `(${persistedChats.length} persisted chats)`);
    }
  }, [presentationId, userId]);

  // Subscribe to SmartCache for live chat updates + auto-persist (localStorage + Firestore)
  useEffect(() => {
    if (!presentationId || !userId) {
      // Don't clear state on transient null (auth token refresh)
      // Only clear if we've never had a user (true initial state)
      return;
    }

    let saveTimer = null;

    const unsubscribe = smartCache.subscribeToField(
      presentationId,
      'chats',
      (chatsData) => {
        // NON-MUTATING sort — never mutate SmartCache's internal array.
        // Pinned chats first, then newest-first within each group.
        const sortedChats = [...(chatsData || [])].sort((a, b) => {
          const pinDiff = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
          if (pinDiff !== 0) return pinDiff;
          const dateA = new Date(a.updatedAt || a.createdAt || 0);
          const dateB = new Date(b.updatedAt || b.createdAt || 0);
          return dateB - dateA;
        });

        setChats(sortedChats);

        // Update current messages from the active chat using the ref (always fresh)
        const chatId = currentChatIdRef.current;
        if (chatId) {
          const activeChat = (chatsData || []).find(chat => chat.id === chatId);
          if (activeChat) {
            if (ownerReadableTranscriptAllowed(activeChat)) {
              setCurrentMessages(normalizeMessages(activeChat.messages || []));
            } else {
              setCurrentMessages(volatileTranscriptRef.current.get(chatId) || []);
            }
          }
          // Do NOT clear messages if chat not found — secondary effect handles that
        }

        // Don't auto-select a previous chat on reload — start fresh
        // Users can access previous chats via the history sidebar
        if (!chatId && sortedChats.length > 0) {
          console.log('[Chat] Previous chats exist but starting fresh (new chat on reload)');
        }

        // Detect deletions (chatIds that vanished since last subscriber call)
        const currentChats = new Map((chatsData || []).map((chat) => [chat.id, chat]));
        for (const [id, deletedChat] of previousChatsRef.current) {
          if (!currentChats.has(id)) {
            pendingRemoteDeletesRef.current.set(id, deletedChat);
            volatileTranscriptRef.current.delete(id);
            publishedRevsRef.current.delete(id);
            metaFingerprintRef.current.delete(id);
          }
        }
        previousChatsRef.current = currentChats;

        // Trailing-throttle persistence: localStorage first (cheap), then
        // Firestore. A throttle (never reset once armed) guarantees a flush
        // at least every interval while a turn streams; the interval tightens
        // to STREAMING_FLUSH_MS while this pane is the live author so peer
        // panes render the turn near-live. Echo safety comes from the
        // per-message publish cursor, live-message ownership, and the meta
        // fingerprint, not from suppressing whole flushes.
        if (saveTimer) return;
        const flushDelay = smartCache.hasLiveMessages() ? STREAMING_FLUSH_MS : IDLE_FLUSH_MS;
        saveTimer = setTimeout(async () => {
          saveTimer = null;
          const latestChats = smartCache.get(presentationId)?.chats || [];

          // localStorage persistence is a full-history stringify, too heavy
          // to run on every 1s streaming flush. Throttle it to ~5s while a
          // turn streams; the first idle flush after the turn always saves.
          const now = Date.now();
          if (!smartCache.hasLiveMessages() || now - lastLocalSaveRef.current >= 5000) {
            lastLocalSaveRef.current = now;
            savePersistedChats(userId, latestChats);
          }

          if (!isChatSyncEnabled()) return;

          // Never publish before the first remote hydrate has merged: a pane
          // seeded from stale localStorage would otherwise upload old copies
          // over newer Firestore history it has not downloaded yet (and
          // other panes' publish cursors would never re-send the newer revs).
          if (!hydratedOnceRef.current) return;

          // Mirror deletes accumulated since the last flush
          const deletes = [...pendingRemoteDeletesRef.current.entries()];
          pendingRemoteDeletesRef.current.clear();
          for (const [id, deletedChat] of deletes) {
            deleteChatRemote(userId, id, deletedChat);
          }

          // Upsert each chat (metadata + messages whose rev passed the cursor)
          for (const chat of latestChats) {
            // Empty chats stay local: connector plumbing chats and just-minted
            // chats have nothing shareable, and uploading them would litter
            // every other pane's chat list with ghosts.
            if (
              (chat.messages || []).length === 0
              && !(isLockerScopedTranscript(chat) && chat.messageCount > 0)
            ) continue;

            const publishedMap = getPublishedMap(chat.id);
            const transcriptMode = transcriptModeForChat(chat);
            if (transcriptMode === TRANSCRIPT_MODE_PENDING) continue;
            const messagesToWrite = transcriptMode === TRANSCRIPT_MODE_ORDINARY
              ? selectMessagesToPublish(chat.messages, publishedMap, isMessageOwned)
              : [];
            const fingerprint = chatMetaFingerprint(chat);
            const skipMetaWrite = metaFingerprintRef.current.get(chat.id) === fingerprint;
            if (messagesToWrite.length === 0 && skipMetaWrite) continue;
            try {
              await upsertChat(userId, chat, messagesToWrite, { skipMetaWrite });
              metaFingerprintRef.current.set(chat.id, fingerprint);
              recordPublishedRevs(publishedMap, messagesToWrite);
            } catch (err) {
              console.warn('[ChatSync] Upsert failed for chat', chat.id, err.message);
            }
          }
        }, flushDelay);
      }
    );

    return () => {
      unsubscribe();
      if (saveTimer) clearTimeout(saveTimer);
    };
  }, [presentationId, userId, switchToChatUI]);

  // Subscribe to Firestore for cross-device chat sync. localhost is
  // intentionally a sandbox: localStorage-only, no Firestore involvement.
  useEffect(() => {
    if (!presentationId || !userId) return;
    if (!isChatSyncEnabled()) return;

    let cancelled = false;
    let firstSnapshot = true;

    const unsub = subscribeChatList(userId, (firestoreChatList) => {
      // Serialize hydrate runs: snapshots can fire while a prior async run is
      // still fetching, and interleaved or out-of-order completions would
      // write stale captured state over newer live state.
      hydrateQueueRef.current = hydrateQueueRef.current.then(async () => {
        if (cancelled) return;

        // Phase 1 (the ONLY awaits): fetch remote messages INCREMENTALLY —
        // only for chats whose remote lastMessageAt advanced past what this
        // pane already hydrated (the live mirror keeps the ACTIVE chat fresh
        // in between). Without the marker, every metadata change re-read
        // every message of every chat on every pane.
        const fetched = await Promise.all(
          firestoreChatList.map(async (fsChat) => {
            const remoteTs = Date.parse(fsChat.updatedAt || '') || 0;
            const marker = hydrateMarkersRef.current.get(fsChat.id) || 0;
            if (remoteTs > 0 && remoteTs <= marker) return [fsChat, null];
            try {
              const localAtRead = (
                smartCache.get(presentationId)?.chats || []
              ).find((chat) => chat.id === fsChat.id);
              let readPolicyChat = fsChat;
              try {
                readPolicyChat = {
                  ...fsChat,
                  transcriptMode: reconcileServerTranscriptMode(
                    localAtRead,
                    fsChat.transcriptMode
                  ),
                };
              } catch {
                readPolicyChat = {
                  ...fsChat,
                  transcriptMode: transcriptModeForChat(localAtRead),
                };
              }
              const messages = await fetchChatMessages(
                userId,
                fsChat.id,
                readPolicyChat
              );
              if (remoteTs > 0) hydrateMarkersRef.current.set(fsChat.id, remoteTs);
              return [fsChat, messages];
            } catch (err) {
              console.warn('[ChatSync] fetchChatMessages failed:', fsChat.id, err.message);
              return [fsChat, null];
            }
          })
        );
        if (cancelled) return;

        // Phase 2 (fully synchronous): read the cache FRESH after the awaits
        // and merge without yielding, so SSE processing or the per-chat
        // mirror cannot slip newer state in between our read and our write.
        const cached = smartCache.get(presentationId);
        const localChats = cached?.chats || [];
        const localChatById = new Map(localChats.map((c) => [c.id, c]));
        const fsChatIds = new Set(firestoreChatList.map((c) => c.id));

        const merged = fetched.map(([fsChat, fsMessages]) => {
          const localChat = localChatById.get(fsChat.id);
          let safeRemoteChat = fsChat;
          try {
            safeRemoteChat = {
              ...fsChat,
              transcriptMode: reconcileServerTranscriptMode(
                localChat,
                fsChat.transcriptMode
              ),
            };
          } catch {
            safeRemoteChat = {
              ...fsChat,
              transcriptMode: transcriptModeForChat(localChat),
            };
          }
          if (!ownerReadableTranscriptAllowed(safeRemoteChat)) {
            const scopedChat = {
              ...(localChat || {}),
              ...safeRemoteChat,
              messages: [],
            };
            metaFingerprintRef.current.set(fsChat.id, chatMetaFingerprint(scopedChat));
            return scopedChat;
          }
          if (!fsMessages) {
            // Skipped (already hydrated to this watermark) or fetch failed:
            // keep local messages, still merge the remote meta fields.
            const carriedChat = localChat
              ? { ...localChat, ...safeRemoteChat, messages: localChat.messages || [] }
              : { ...safeRemoteChat, messages: [] };
            metaFingerprintRef.current.set(fsChat.id, chatMetaFingerprint(carriedChat));
            return carriedChat;
          }

          // Same per-message rules as the live mirror: owned live messages
          // and fresher local revs win; everything else comes from Firestore.
          const messages = mergeRemoteMessages(localChat?.messages, fsMessages, isMessageOwned);

          // Record downloaded copies on the publish cursor so Effect 2's
          // flush does not echo them straight back.
          recordPublishedRevs(getPublishedMap(fsChat.id), fsMessages);

          // Spread the local chat first so local-only fields (sessionId,
          // sdkSessionId, ...) survive; the remote meta doc only carries the
          // shared subset (title, flags, timestamps).
          const mergedChat = { ...(localChat || {}), ...safeRemoteChat, messages };
          // Prime the meta fingerprint to the remote-driven state so the
          // flush only writes meta after a genuine local change.
          metaFingerprintRef.current.set(fsChat.id, chatMetaFingerprint(mergedChat));
          return mergedChat;
        });

        // Preserve local-only chats (just created on this device, not yet
        // uploaded, or genuinely sandboxed if Firestore sync had been off).
        const localOnlyChats = localChats.filter((c) => !fsChatIds.has(c.id));
        const finalChats = [...merged, ...localOnlyChats];

        smartCache.set(presentationId, {
          ...(cached || {}),
          id: presentationId,
          title: cached?.title || 'PowerPoint Plugin',
          chats: finalChats,
          userId
        });
        if (finalChats.some((chat) => isLockerScopedTranscript(chat))) {
          savePersistedChats(userId, finalChats);
        }

        // Remote state has been merged at least once: uploads may begin.
        hydratedOnceRef.current = true;

        // First snapshot: one-time additive migration, pushing any local-only
        // chats up to Firestore so they appear on the user's other devices.
        if (firstSnapshot) {
          firstSnapshot = false;
          for (const chat of localOnlyChats) {
            try {
              const transcriptMode = transcriptModeForChat(chat);
              if (transcriptMode === TRANSCRIPT_MODE_PENDING) continue;
              const publishedMap = getPublishedMap(chat.id);
              const migrateMessages = transcriptMode === TRANSCRIPT_MODE_ORDINARY
                ? selectMessagesToPublish(chat.messages, publishedMap, isMessageOwned)
                : [];
              await upsertChat(userId, chat, migrateMessages, {});
              recordPublishedRevs(publishedMap, migrateMessages);
            } catch (err) {
              console.warn('[ChatSync] Migration upload failed:', chat.id, err.message);
            }
          }
        }
      }).catch((hydrateErr) => {
        console.warn('[ChatSync] Chat-list hydrate failed:', hydrateErr?.message);
      });
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [presentationId, userId]);

  // Update messages immediately when user switches chats
  useEffect(() => {
    if (!presentationId || !currentChatId) return;

    const cached = smartCache.get(presentationId);
    if (cached?.chats) {
      const activeChat = cached.chats.find(chat => chat.id === currentChatId);
      if (activeChat) {
        if (ownerReadableTranscriptAllowed(activeChat)) {
          setCurrentMessages(normalizeMessages(activeChat.messages || []));
          setScopedTranscriptState('idle');
        } else {
          setCurrentMessages(volatileTranscriptRef.current.get(currentChatId) || []);
          setScopedTranscriptState(
            isLockerScopedTranscript(activeChat) ? 'loading' : 'pending'
          );
        }
      } else {
        setCurrentMessages([]);
      }
    }
  }, [presentationId, currentChatId]);

  // The active transcript has one of two mirrors. Ordinary chats use the
  // existing owner-readable Firestore message collection. Scope-bound chats
  // poll an authenticated server projection that revalidates access on every
  // request and remains React-only.
  const activeTranscriptMode = transcriptModeForChat(
    chats.find((chat) => chat.id === currentChatId)
  );
  useEffect(() => {
    if (!presentationId || !userId || !currentChatId) return undefined;

    const chatId = currentChatId;
    const cached = smartCache.get(presentationId);
    const activeChat = (cached?.chats || []).find((chat) => chat.id === chatId);
    const transcriptMode = activeTranscriptMode;

    if (transcriptMode === TRANSCRIPT_MODE_PENDING) {
      setScopedTranscriptState('pending');
      return undefined;
    }

    if (transcriptMode === TRANSCRIPT_MODE_LOCKER_SCOPED) {
      setScopedTranscriptState('loading');
      const unsubscribe = subscribeScopedTranscript({
        chatId,
        getAuthToken: () => currentUser.getIdToken(),
        onMessages: (messages, {
          truncated,
          accessFiltered,
        } = {}) => {
          if (pendingRemoteDeletesRef.current.has(chatId)) return;
          if (accessFiltered) {
            clearVolatileTranscript(chatId);
            setScopedTranscriptState('locked');
            return;
          }
          const localMessages = volatileTranscriptRef.current.get(chatId) || [];
          const localById = new Map(localMessages.map((message) => [message.id, message]));
          const remoteIds = new Set(messages.map((message) => message.id));
          const preserveCurrentTurnMessage = (messageId) => {
            if (isMessageOwned(messageId)) return true;
            const localMessage = localById.get(messageId);
            return (
              !remoteIds.has(messageId)
              && localMessage?.sender === 'user'
              && typeof localMessage.requestStartTime === 'number'
              && Date.now() - localMessage.requestStartTime < 5 * 60 * 1000
            );
          };
          const displayedMessages = mergeAuthoritativeMessages(
            messages,
            localMessages,
            preserveCurrentTurnMessage
          );
          replaceVolatileTranscript(chatId, displayedMessages);
          setScopedTranscriptState(truncated ? 'ready_truncated' : 'ready');
        },
        onError: (error) => {
          clearVolatileTranscript(chatId);
          setScopedTranscriptState('locked');
          console.warn('[ChatSync] Secure transcript unavailable:', error?.code || error?.message);
        },
      });
      return () => {
        unsubscribe();
        // Never keep an inactive scope-bound transcript available for a
        // later render. Reopening the chat must pass a fresh server-side ACL
        // and source-version check before any prior content is shown.
        clearVolatileTranscript(chatId);
      };
    }

    setScopedTranscriptState('idle');
    if (!isChatSyncEnabled()) return undefined;

    const unsubscribe = subscribeChatMessages(userId, chatId, (remoteMessages) => {
      if (!Array.isArray(remoteMessages) || remoteMessages.length === 0) return;

      // A chat deleted locally must not be resurrected by a straggling
      // snapshot while its remote docs are still being torn down.
      if (pendingRemoteDeletesRef.current.has(chatId)) return;

      const cached = smartCache.get(presentationId);
      const chat = (cached?.chats || []).find((c) => c.id === chatId);
      const messages = mergeRemoteMessages(chat?.messages, remoteMessages, isMessageOwned);

      // Record downloaded copies on the publish cursor BEFORE the cache write
      // so the flush this write triggers cannot echo them back to Firestore.
      recordPublishedRevs(getPublishedMap(chatId), remoteMessages);

      // Stable updatedAt (newest message timestamp) so mirror merges do not
      // churn chat metadata with this pane's wall clock.
      const newestTs = messages.length > 0 ? messages[messages.length - 1].timestamp : undefined;

      if (chat) {
        // Prime the meta fingerprint on FIRST observation only (a pane that
        // has already flushed or locally edited this chat keeps its own
        // fingerprint, so pending local meta changes still upload).
        if (!metaFingerprintRef.current.has(chatId)) {
          const mergedChat = {
            ...chat,
            messages,
            ...(newestTs ? { updatedAt: newestTs } : {})
          };
          metaFingerprintRef.current.set(chatId, chatMetaFingerprint(mergedChat));
        }
        smartCache.updateChat(presentationId, chatId, {
          messages,
          ...(newestTs ? { updatedAt: newestTs } : {})
        });
      } else {
        // The chat only exists remotely so far (a peer pane created it):
        // seed it locally; the chat-list subscription fills in its metadata.
        const seededChat = {
          id: chatId,
          name: 'New chat',
          messages,
          ...(newestTs ? { createdAt: newestTs, updatedAt: newestTs } : {})
        };
        metaFingerprintRef.current.set(chatId, chatMetaFingerprint(seededChat));
        smartCache.createChat(presentationId, seededChat);
      }
    }, activeChat);

    return unsubscribe;
  }, [
    clearVolatileTranscript,
    activeTranscriptMode,
    currentChatId,
    currentUser,
    presentationId,
    replaceVolatileTranscript,
    userId,
  ]);

  // Repository operations. `chatData` lets callers stamp extra fields onto
  // the new chat (e.g. `origin`: which Office app it was started in).
  const createNewChat = useCallback(async (initialMessage = null, chatData = {}) => {
    if (!presentationId) return;
    const result = await presentationRepository.createChat(presentationId, {
      ...chatData,
      transcriptMode: TRANSCRIPT_MODE_PENDING,
    });
    const newChat = result?.chats?.[result.chats.length - 1];
    if (newChat) {
      switchToChatUI(newChat.id);
      if (initialMessage) {
        upsertVolatileTranscriptMessage(newChat.id, initialMessage);
      }
    }
    return result;
  }, [presentationId, switchToChatUI, upsertVolatileTranscriptMessage]);

  const switchToChat = useCallback((chatId) => {
    switchToChatUI(chatId);
  }, [switchToChatUI]);

  const addMessageToChat = useCallback(async (chatId, message) => {
    if (!presentationId || !chatId) return;
    const chat = (smartCache.get(presentationId)?.chats || [])
      .find((candidate) => candidate.id === chatId);
    if (!ownerReadableTranscriptAllowed(chat)) {
      return upsertVolatileTranscriptMessage(chatId, message);
    }
    return presentationRepository.addMessage(presentationId, chatId, message);
  }, [presentationId, upsertVolatileTranscriptMessage]);

  const addMessage = useCallback(async (message) => {
    // Use ref to always get the latest chatId (avoids stale closure issues)
    const chatId = currentChatIdRef.current;
    if (!chatId) {
      console.log('[Chat] No currentChatId in addMessage — skipping');
      return;
    }
    return addMessageToChat(chatId, message);
  }, [addMessageToChat]);

  const applyServerTranscriptMode = useCallback(async (
    chatId,
    serverMode,
    sessionMetadata = {}
  ) => {
    if (!presentationId || !chatId) return null;
    const cachedChat = (smartCache.get(presentationId)?.chats || [])
      .find((candidate) => candidate.id === chatId);
    const nextMode = reconcileServerTranscriptMode(cachedChat, serverMode);
    const volatileMessages = volatileTranscriptRef.current.get(chatId) || [];

    if (nextMode === TRANSCRIPT_MODE_LOCKER_SCOPED) {
      if ((cachedChat?.messages || []).length > 0) {
        await presentationRepository.clearChatMessages(presentationId, chatId);
      }
      await presentationRepository.updateChat(presentationId, chatId, {
        ...sessionMetadata,
        name: LOCKER_SCOPED_SAFE_CHAT_TITLE,
        title: LOCKER_SCOPED_SAFE_CHAT_TITLE,
        transcriptMode: nextMode,
        messageCount: volatileMessages.length,
      });
      const updatedChats = smartCache.get(presentationId)?.chats || [];
      savePersistedChats(userId, updatedChats);
      return nextMode;
    }

    await presentationRepository.updateChat(presentationId, chatId, {
      ...sessionMetadata,
      transcriptMode: TRANSCRIPT_MODE_ORDINARY,
    });
    for (const message of volatileMessages) {
      await presentationRepository.addMessage(presentationId, chatId, message);
    }
    volatileTranscriptRef.current.delete(chatId);
    if (currentChatIdRef.current === chatId) {
      const ordinaryChat = (smartCache.get(presentationId)?.chats || [])
        .find((candidate) => candidate.id === chatId);
      setCurrentMessages(normalizeMessages(ordinaryChat?.messages || []));
    }
    return TRANSCRIPT_MODE_ORDINARY;
  }, [presentationId, userId]);

  const clearCurrentChatMessages = useCallback(async () => {
    if (!presentationId || !currentChatId) return;
    const chat = (smartCache.get(presentationId)?.chats || [])
      .find((candidate) => candidate.id === currentChatId);
    if (!ownerReadableTranscriptAllowed(chat)) {
      clearVolatileTranscript(currentChatId);
      return;
    }
    return presentationRepository.clearChatMessages(presentationId, currentChatId);
  }, [clearVolatileTranscript, currentChatId, presentationId]);

  const updateChatMetadata = useCallback(async (chatId, updates) => {
    if (!presentationId || !chatId) return;
    return presentationRepository.updateChat(presentationId, chatId, updates);
  }, [presentationId]);

  const renameChatThread = useCallback(async (chatId, newName) => {
    if (!presentationId) return;
    return presentationRepository.renameChat(presentationId, chatId, newName);
  }, [presentationId]);

  const deleteChatThread = useCallback(async (chatId) => {
    if (!presentationId) return;
    const chat = (smartCache.get(presentationId)?.chats || [])
      .find((candidate) => candidate.id === chatId);
    if (
      isLockerScopedTranscript(chat)
      || isTranscriptPolicyPending(chat)
    ) {
      const authToken = await currentUser.getIdToken();
      // The scoped route deletes the durable opencode snapshot as part of its
      // own external-history cleanup, so it is not repeated here.
      await deleteScopedTranscript({ chatId, authToken });
      clearVolatileTranscript(chatId);
    } else {
      // ORDINARY chats are deleted straight into Firestore below, and ordinary
      // chats are the only ones an opencode turn can run on. Their durable
      // transcript lives in the workspace bucket, where a Firestore delete
      // cannot reach it, so it is deleted here or never. Never blocking: the
      // user asked to delete the chat.
      let authToken = null;
      try {
        authToken = await currentUser?.getIdToken?.();
      } catch {
        authToken = null;
      }
      if (authToken) {
        await deleteOpencodeChatHistory({ chatId, authToken }).catch(() => false);
      }
    }
    return presentationRepository.deleteChat(presentationId, chatId);
  }, [clearVolatileTranscript, currentUser, presentationId]);

  const getCurrentChatMessages = useCallback(() => {
    return currentMessages;
  }, [currentMessages]);

  const initializeChatWithWelcome = useCallback(async () => {
    if (!presentationId) return;
    const welcomeMessage = presentationRepository.createWelcomeMessage();
    if (currentChatId) {
      return addMessageToChat(currentChatId, welcomeMessage);
    } else {
      return createNewChat(welcomeMessage);
    }
  }, [addMessageToChat, presentationId, currentChatId, createNewChat]);

  return {
    chats,
    currentChatId,
    currentMessages,
    presentationId,
    createNewChat,
    switchToChat,
    renameChatThread,
    deleteChatThread,
    addMessage,
    addChatMessage: addMessage,
    addMessageToChat,
    applyServerTranscriptMode,
    scopedTranscriptState,
    updateChatMetadata,
    getCurrentChatMessages,
    clearCurrentChatMessages,
    initializeChatWithWelcome
  };
}
